<template>
  <div class="w-full h-screen bg-white overflow-hidden">
    <div class="lg:flex">
      <!-- Vertical nav for larger screens -->
      <div class="hidden lg:block">
        <UserDesktopNav
          :sidebar-links="state.sidebarLinks"
          :profile-options="state.profileOptions"
          :profile="state.profile"
          @update:profile-option-clicked="handleSelect"
        />
      </div>
      <!-- Horizontal nav for tablets and smaller screens -->
      <div class="shadow-md block lg:hidden mb-2">
        <UserMobileNav :sidebar-links="state.sidebarLinks" :profile-options="state.profileOptions" @update:profile-option-clicked="handleSelect" />
      </div>
      <DashHeader>
        <div class="w-full h-full overflow-y-auto">
          <div class="bg-white overflow-x-hidden w-full flex flex-col max-h-screen h-full px-5">
            <div v-show="appContentStore.state.header" class="font-bold text-xl py-5">
              <p v-text="appContentStore.state.header" />
            </div>
            <slot />
          </div>
        </div>
      </DashHeader>
    </div>
  </div>
</template>

<script lang="ts">
import { Auth0VueClient, useAuth0 } from "@auth0/auth0-vue";
import { ApolloProvider } from "@vue/apollo-option";
import { DropdownMixedOption } from "naive-ui/lib/dropdown/src/interface";
import { defineComponent, h, inject, onMounted, reactive, watch } from "vue";
import { Router, useRouter } from "vue-router";
import { Store, useStore } from "vuex";

import DashHeader from "../components/dashboard/DashHeader.vue";
import UserDesktopNav from "../components/dashboard/UserDesktopNav.vue";
import UserMobileNav from "../components/dashboard/UserMobileNav.vue";
import { isOrganizationIntegrationAuthenticated } from "../composables/integrations";
import { useAppContentStore } from "../store/app-content";
import { IntegrationServiceEnum } from "../types/integrations";
import { IAuthProfile, TSidebarLink } from "../types/momentum";
import { containsTrueValue, formatTime, identifyUser } from "../utils/common-js";
import { canRoleUseAdminPage, isExactRoleViewer } from "../utils/roles";

export default defineComponent({
  components: {
    DashHeader,
    UserDesktopNav,
    UserMobileNav,
  },
  setup() {
    type TState = {
      profile: IAuthProfile;
      profileOptions: DropdownMixedOption[];
      sidebar: boolean;
      sidebarLinks: TSidebarLink[];
    };
    const store: Store<any> = useStore();
    const router: Router = useRouter();
    const { logout }: Auth0VueClient = useAuth0();
    const apollo: ApolloProvider = inject<ApolloProvider>("apollo");
    const authUser = store.state.authUser;
    const appContentStore = useAppContentStore();
    const state: TState = reactive({
      profile: null,
      profileOptions: [
        {
          key: "header",
          render: renderUserLabel,
          type: "render",
        },
        {
          key: "integrations",
          label: "Individual Account Connect",
        },
        {
          key: "sign-out",
          label: "Sign Out",
        },
      ],
      sidebar: false,
      sidebarLinks: [
        {
          activeRoutes: ["account-briefs", "account-brief-view"],
          featureFlag: "generic-ui-flag",
          icon: "IconCalendarDays",
          routeName: "account-briefs",
          title: "Accounts",
        },
        {
          activeRoutes: ["user-call-library", "user-playlist-details"],
          featureFlag: "",
          icon: "IconVideo",
          routeName: "user-call-library",
          title: "Calls",
        },
        {
          activeRoutes: ["autopilot"],
          featureFlag: "",
          icon: "IconAutoPilot",
          routeName: "autopilot",
          title: "Autopilot",
        },
        {
          activeRoutes: ["email-follow-up", "user-email-follow-up-config"],
          featureFlag: "",
          icon: "IconEmail",
          routeName: "email-follow-up",
          title: "Email Follow-Up",
        },
      ],
    });

    onMounted(() => {
      identifyUser(store.state.authUser);
      if (canRoleUseAdminPage(authUser)) {
        const admin = {
          key: "admin",
          label: "Momentum Admin Page",
        };
        state.profileOptions.splice(1, 0, admin);
      }
      // If the user is a viewer, remove the integrations, zoom settings options, and accounts
      if (isExactRoleViewer(authUser)) {
        state.profileOptions = state.profileOptions.filter(
          (item) => item.key !== "integrations" && item.key !== "zoom-settings"
        );
        state.sidebarLinks = state.sidebarLinks.filter((item) => item.routeName !== "account-briefs");
      } else {
        renderZoomUserSettings();
      }
      state.profile = store.state.profile;
    });

    watch(
      () => store.state.profile,
      (profile) => {
        state.profile = profile;
      }
    );

    async function handleSelect(value: string): Promise<void> {
      switch (value) {
        case "user-call-library":
          router.push({ name: "user-call-library" });
          break;
        case "admin":
          router.push({ name: "ai-summary-notifications" });
          break;
        case "integrations":
          router.push({ name: "individual-account-connect" });
          break;
        case "zoom-settings":
          router.push({ name: "zoom-settings" });
          break;
        case "sign-out":
          await logout({ logoutParams: { returnTo: window.location.origin + "/sign-in" } });
          break;
      }
    }

    function renderUserLabel() {
      return h(
        "div",
        {
          style: {
            alignItems: "center",
            backgroundColor: "#172E5B",
            color: "#fff",
            display: "flex",
            margin: "-5px 0 5px 0",
            padding: "8px 12px",
          },
        },
        [
          h("div", null, [
            h("div", null, [authUser && state.profile?.name]),
            h("div", null, [authUser && state.profile?.email]),
          ]),
        ]
      );
    }

    async function renderZoomUserSettings(): Promise<void> {
      const zoomIntegrationIsAuthenticated = await isOrganizationIntegrationAuthenticated(
        apollo,
        IntegrationServiceEnum.ZOOM,
        store
      );
      if (!zoomIntegrationIsAuthenticated) {
        return Promise.resolve();
      }
      if (!hasUserLicense()) {
        return Promise.resolve();
      }

      const signOutIndex = state.profileOptions.findIndex((item) => item.key === "sign-out");
      state.profileOptions.splice(signOutIndex - 1, 0, { key: "zoom-settings", label: "Zoom Settings" }); //settings right above sign out link
    }

    function hasUserLicense(): boolean {
      return containsTrueValue(store.state.profile?.userLicense);
    }

    return { appContentStore, authUser, formatTime, handleSelect, state };
  },
});
</script>

<style>
.router-link-exact-active .icon-wrapper,
.active-route .icon-wrapper,
li.active-route {
  @apply bg-mm-colors-sidebarHighlight text-mm-colors-textPrimary;
}

.active-route .active-route-link-title,
.active-route .active-route-link-icon {
  @apply text-mm-colors-textPrimary;
}

/* 2xl = 1536px */
@media (min-width: 1536px) {
  .active-route .active-route-link {
    @apply bg-mm-colors-sidebarHighlight text-mm-colors-textPrimary;
  }
}

.sidebar-leave-active {
  left: -100%;
  transition: all 1s ease-out;
}

.sidebar-enter-active {
  right: 100%;
  transition: all 1s ease-in;
}
</style>
